"use client";
import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  X,
  Plus,
  Calendar,
  Clock,
  Users,
  Flag,
  Target,
  Tag,
  FileText,
  Link,
  Activity,
  ChevronDown,
  Copy,
  MessageCircle,
  ImagePlus,
  Clipboard,
  Pause,
  Play
} from 'lucide-react';
import { useTaskContext } from '../../context/TaskContext';
import { useProjects } from '../../context/ProjectContext';
import { useEmployee } from '../../context/EmployeeContext';
import { useComments } from '../../context/CommentsContext';
import { CDN_URL } from '../../config/apiConfig';

const TaskModal = ({ isOpen, onClose, prefillData: initialTaskData = null, viewOnly = false, onSaved }) => {
  const modalRef = useRef(null);
  const assigneeDropdownRef = useRef(null);
  const imageInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    id: '',
    task_id : '',
    title: '',
    description: '',
    comments: '',
    status: 'To Do',
    startDate: '',
    endDate: '',
    timeEstimate: '',
    links: [],
    assignees: [],
    priority: '',
    sprintPoints: '',
    tags: [],
    projectId: '',
    images: []
  });

  // UI state
  const [comment, setComment] = useState('');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', color: 'bg-blue-500' });
  const [showTagInput, setShowTagInput] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);

  // Image upload state (new files staged for upload + removal tracking)
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [pasteFlash, setPasteFlash] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Context hooks
  const { tasks, addTask, updateTask, fetchTasks, toggleTaskHold, fetchTaskHolds, taskHoldStatus } = useTaskContext();
  const { projects } = useProjects();
  const { employees } = useEmployee();
  const { comments, loading: commentsLoading, fetchComments, addComment } = useComments();

  // Hold/Resume state
  const [holdLoading, setHoldLoading] = useState(false);
  const isOnHold = !!taskHoldStatus[formData.id];

  const getTodayDateTimeLocal = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Sample data structure matching the original design
  const defaultTaskData = {
    id: '',
    task_id:'',
    title: '',
    description: '',
    status: 'To Do',
    startDate: '',
    endDate: '',
    timeEstimate: '',
    links: [],
    assignees: [],
    priority: '',
    sprintPoints: '',
    tags: [],
    projectId: ''
  };

  const statusOptions = [
    { value: 'To Do', label: 'To Do', color: 'bg-gray-500', textColor: 'text-white' },
    { value: 'Inprogress', label: 'In Progress', color: 'bg-sky-600', textColor: 'text-white' },
    { value: 'Completed', label: 'Complete', color: 'bg-green-600', textColor: 'text-white' },
    { value: 'Inreview', label: 'Inreview', color: 'bg-blue-600', textColor: 'text-white' },
    { value: 'QA', label: 'qa', color: 'bg-yellow-600', textColor: 'text-white' }
  ];

  const priorityOptions = [
    { value: '', label: 'Select Priority', color: 'bg-gray-100', textColor: 'text-gray-800' },
    { value: 'low', label: 'Low', color: 'bg-green-500', textColor: 'text-white' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-500', textColor: 'text-white' },
    { value: 'high', label: 'High', color: 'bg-red-500', textColor: 'text-white' },
    { value: 'urgent', label: 'Urgent', color: 'bg-purple-500', textColor: 'text-white' }
  ];

  const tagColors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
    'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-gray-500'
  ];

  // Resolve an image reference (bare filename from DB, full URL, or blob preview) to a renderable src
  const getImageUrl = (img) => {
    if (!img) return '';
    if (/^(https?:)?\/\//.test(img) || img.startsWith('blob:')) return img;
    return `${CDN_URL}/${img}`;
  };

  // `prefillData` arrives in one of two shapes: a raw bug/ticket (when
  // converting a not-yet-converted request — bug_title, bug_description,
  // estimated_hours, no task_id) or RequestManagement's `livePrefillData`
  // (an already-converted task — title, description, start_date, task_id,
  // assignee_name/user_id instead of an `assignees` array). Normalize
  // either into the fields this form's inputs read from `formData`.
  const normalizeTaskForForm = (t) => {
    if (!t) return defaultTaskData;

    const rawTags = Array.isArray(t.tags)
      ? t.tags
      : Array.isArray(t.type)
        ? t.type
        : [];
    const normalizedTags = rawTags.map((tag, i) => {
      if (tag && typeof tag === 'object') {
        return {
          id: tag.id ?? `${i}-${tag.name}`,
          name: tag.name ?? String(tag),
          color: tag.color || tagColors[i % tagColors.length]
        };
      }
      return {
        id: `${i}-${tag}`,
        name: tag,
        color: tagColors[i % tagColors.length]
      };
    });

    let normalizedAssignees = Array.isArray(t.assignees) ? t.assignees : [];
    if (normalizedAssignees.length === 0 && t.user_id) {
      const name = (t.assignee_name || '').trim();
      const parts = name.split(' ').filter(Boolean);
      const initials = parts.map(p => p[0]?.toUpperCase() || '').join('') || '?';
      normalizedAssignees = [{
        id: t.user_id,
        name,
        initials,
        avatar: t.assignee_avatar || t.avatar || null,
        color: 'bg-blue-400'
      }];
    }

    return {
      id: t.id ?? '',
      task_id: t.task_id ?? '',
      title: t.title ?? t.bug_title ?? '',
      description: t.description ?? t.bug_description ?? '',
      comments: t.comments ?? '',
      // A not-yet-converted bug/request carries its OWN status vocabulary
      // (Open, Fixed, ReadyForProduction, ...), which shares no values with
      // this form's task statusOptions (To Do, Inprogress, Completed, ...).
      // Passing it straight through left the required <select> holding a
      // value that matches none of its <option>s — so it rendered with
      // nothing selected and native HTML5 validation silently blocked the
      // whole submit, with no visible error, on every conversion attempt.
      // Only trust an inherited status once the record is already a task
      // (has a task_id); otherwise start fresh at "To Do".
      status: (t.task_id && t.status && String(t.status).trim() !== '') ? t.status : 'To Do',
      startDate: t.startDate || t.start_date || getTodayDateTimeLocal(),
      endDate: t.endDate ?? t.end_date ?? '',
      timeEstimate: t.timeEstimate ?? t.time ?? t.estimated_hours ?? '',
      links: Array.isArray(t.links)
        ? t.links
        : (t.links ? [t.links] : []),
      assignees: normalizedAssignees,
      priority: t.priority ?? '',
      sprintPoints: t.sprintPoints ?? t.points ?? '',
      tags: normalizedTags,
      projectId: t.projectId ?? t.project_id ?? '',
      images: Array.isArray(t.images) ? t.images : []
    };
  };

  // Responsive detection
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // On desktop, show sidebar only if editing. On mobile, hide it initially
      if (!mobile && isEditing) {
        setShowSidebar(true);
      } else {
        setShowSidebar(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isEditing]);

  // Click outside handler for assignee dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        setShowAssigneeDropdown(false);
        setAssigneeSearch('');
      }
    };

    if (showAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAssigneeDropdown]);

  // Initialize modal data
  useEffect(() => {
  if (isOpen) {
    const taskToLoad = normalizeTaskForForm(initialTaskData);
    setFormData(taskToLoad);

    const editing = !!taskToLoad.task_id;
    setIsEditing(editing);

    // Reset any staged image uploads/removals whenever the modal (re)opens
    setNewImageFiles([]);
    setNewImagePreviews([]);
    setRemovedImages([]);

    if (editing && taskToLoad.id) {
      fetchComments(taskToLoad.id);
      fetchTaskHolds(taskToLoad.id);
    }
    document.body.style.overflow = "hidden";

  } else {
    document.body.style.overflow = "unset";

    setShowSidebar(false);
  }

  return () => {
    document.body.style.overflow = "unset";
  };
}, [isOpen, initialTaskData, fetchComments, fetchTaskHolds]);

  // Revoke object URLs for staged image previews when they change/unmount
  useEffect(() => {
    return () => {
      newImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [newImagePreviews]);

  // Paste-to-upload support while the modal is open
  useEffect(() => {
    if (!isOpen) return;
    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const imageFiles = items
        .filter((item) => item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (imageFiles.length > 0) {
        addImageFiles(imageFiles);
        setPasteFlash(true);
        setTimeout(() => setPasteFlash(false), 600);
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen]);

  const addImageFiles = (files) => {
    setNewImageFiles((prev) => [...prev, ...files]);
    setNewImagePreviews((prev) => [
      ...prev,
      ...files.map((f) => URL.createObjectURL(f))
    ]);
  };

  const removeNewImage = (index) => {
    setNewImageFiles((prev) => prev.filter((_, i) => i !== index));
    setNewImagePreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeExistingImage = (index) => {
    const removed = formData.images[index];
    setRemovedImages((prev) => [...prev, removed]);
    updateField('images', formData.images.filter((_, i) => i !== index));
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith('image/')
    );
    if (files.length) addImageFiles(files);
  };

  const handleImageInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) addImageFiles(files);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const baseFields = {
      project_id: formData.projectId,
      title: formData.title,
      user_id: formData.assignees[0]?.id || '',
      start_date: formData.startDate,
      end_date: formData.endDate,
      // Left blank in the UI on purpose (neither field is required here) —
      // fall back to '0' so an empty value doesn't trip a NOT NULL/empty
      // check on the save endpoint.
      time: formData.timeEstimate || '0',
      links: formData.links,
      points: formData.sprintPoints || '0',
      priority: formData.priority,
      description: formData.description,
      tags: formData.tags.map(tag => tag.name).join(','),
      status: formData.status,
      // When converting a request into a task (not editing an already-
      // converted one), formData.id is the originating bug/request's row
      // id — the backend's `tasks.bug_id` column is NOT NULL, so this
      // must be sent to both satisfy that constraint and actually link
      // the new task back to the request it came from.
      ...(!isEditing && formData.id ? { bug_id: formData.id } : {}),
    };

    // If there are new images to upload or existing images were removed,
    // send as FormData (multipart) instead of a plain JSON object.
    let payload;
    const hasImageChanges = newImageFiles.length > 0 || removedImages.length > 0;

    if (hasImageChanges) {
      const fd = new FormData();
      Object.entries(baseFields).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          val.forEach((v) => fd.append(`${key}[]`, v));
        } else {
          fd.append(key, val ?? '');
        }
      });
      newImageFiles.forEach((file) => fd.append('images[]', file));
      (formData.images || []).forEach((img) => fd.append('existingImagesArray[]', img));
      removedImages.forEach((img) => fd.append('removedImages[]', img));
      payload = fd;
    } else {
      payload = baseFields;
    }

    setIsSubmitting(true);
    try {
      let response;
      if (isEditing && formData.id) {
        response = await updateTask(formData.id, payload);
      } else {
        response = await addTask(payload);
      }
      if (response?.status === 200 || response?.status === 201) {
        await fetchTasks();
        onSaved?.();
        onClose();
      } else {
        console.error('❌ Failed to save task:');
      }
    } catch (error) {
      console.error('💥 Error saving task:');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleHold = async () => {
    if (!formData.id || holdLoading) return;
    setHoldLoading(true);
    try {
      await toggleTaskHold(formData.id);
    } finally {
      setHoldLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
  e.preventDefault();

    try {
      await addComment(formData.id, comment.trim()); // <-- send STRING ONLY
      setComment("");
    } catch (error) {
      console.error("❌ Failed to submit comment:", error);
    }
  
};


  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" && tagInput.trim() !== "") {
      e.preventDefault();
      const newTag = {
        id: Date.now().toString(),
        name: tagInput.trim(),
        color: tagColors[Math.floor(Math.random() * tagColors.length)]
      };
      if (!formData.tags.find(tag => tag.name === newTag.name)) {
        updateField('tags', [...(formData.tags || []), newTag]);
      }
      setTagInput("");
    }
  };

  const removeTag = (tagId) => {
    updateField('tags', formData.tags?.filter(tag => tag.id !== tagId) || []);
  };

  const addAssignee = (employee) => {
    const firstInitial = employee.first_name?.[0]?.toUpperCase() || '';
    const lastInitial = employee.last_name?.[0]?.toUpperCase() || '';
    const assignee = {
      id: employee.empId,
      name: `${employee.first_name || ''} ${employee.last_name || ''}`.trim(),
      initials: `${firstInitial}${lastInitial}`,
      avatar: employee.avatar || null,
      color: `bg-${['violet', 'blue', 'green', 'yellow', 'red', 'purple'][Math.floor(Math.random() * 6)]}-400`
    };

    if (!formData.assignees.find(a => a.id === assignee.id)) {
      updateField('assignees', [...formData.assignees, assignee]);
    }
    setShowAssigneeDropdown(false);
    setAssigneeSearch('');
  };

  const removeAssignee = (assigneeId) => {
    updateField('assignees', formData.assignees.filter(a => a.id !== assigneeId));
  };

  const filteredEmployees = employees.filter(emp =>
    `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const formatDateTime = (dateString) => {
    if (!dateString) return "No Time";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "No Time" : date.toLocaleString();
  };

  const getStatusOption = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  const getPriorityOption = (priority) => {
    return priorityOptions.find(p => p.value === priority) || priorityOptions[0];
  };

  const getColorClasses = (colorString) => {
    const colorMap = {
      'bg-gray-500': 'bg-gray-500 text-white',
      'bg-sky-600': 'bg-sky-600 text-white',
      'bg-green-600': 'bg-green-600 text-white',
      'bg-green-500': 'bg-green-500 text-white',
      'bg-yellow-500': 'bg-yellow-500 text-white',
      'bg-red-500': 'bg-red-500 text-white',
      'bg-purple-500': 'bg-purple-500 text-white',
      'bg-gray-100': 'bg-gray-100 text-gray-800',
      'bg-blue-600': 'bg-blue-600 text-white',
      'bg-yellow-600': 'bg-yellow-600 text-white',
    };
    return colorMap[colorString] || 'bg-gray-100 text-gray-800';
  };

  const copyTaskId = () => {
    navigator.clipboard.writeText(formData.id);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  if (!isOpen) return null;

  const formatForDatetimeLocal = (dateString) => {
    if (!dateString) return '';

    // Pull the date/time digits straight out of the string instead of
    // routing them through `new Date(...)`. A Date always interprets a
    // string as either UTC (trailing Z / +offset) or local time and
    // converts it when read back via getHours()/getDate() — for a value
    // that's really just "no time was set" (a bare date, or a backend
    // default of literal midnight in whatever format it happens to use)
    // that conversion silently shifts it by the browser's timezone offset
    // (00:00 UTC renders as 05:30 in IST) or even rolls it onto the wrong
    // day. This app also writes these values as plain, timezone-less
    // "YYYY-MM-DDTHH:mm" strings in the first place, so reading the same
    // shape back literally keeps the round trip symmetric.
    const match = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
    if (match) {
      const [, year, month, day, hours, minutes] = match;
      return `${year}-${month}-${day}T${hours ?? '00'}:${minutes ?? '00'}`;
    }

    // Fallback for any non-ISO-ish date string.
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-1 sm:p-2 md:p-4">
      <div className="fixed inset-0 bg-black bg-opacity-60" onClick={onClose} />

      <div
        ref={modalRef}
        className="relative w-full h-full sm:h-[95vh] bg-white sm:rounded-xl shadow-2xl flex overflow-hidden max-w-7xl"
      >
        {/* Main Content */}
        <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
          isMobile && showSidebar ? 'hidden' : 'flex'
        }`}>
          
            <form onSubmit={handleSubmit} className="h-full flex flex-col min-h-0">
          {/* Header */}
          <div className="flex justify-between items-center p-3 md:p-4 border-b bg-white shrink-0">
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-semibold text-stone-500">
                <div className="px-2 py-1 bg-zinc-100 border border-zinc-100 rounded text-xs font-mono">
                  {formData.id}
                </div>
                <button
                  onClick={copyTaskId}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Copy Task ID"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
              <input
                type="text" required
                value={formData.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                className="text-base sm:text-lg md:text-xl font-semibold border-none outline-none bg-transparent flex-1 min-w-0 text-neutral-800"
                placeholder="Task title..."
              />
            </div>
            <div className="flex items-center gap-2">
              {isEditing && (
                <button
                  onClick={toggleSidebar}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors relative"
                  title="Toggle Comments"
                >
                  <MessageCircle className="w-5 h-5" />
                  {comments.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                      {comments.length}
                    </span>
                  )}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Duplicate Tasks Banner */}
          <div className="mx-3 md:mx-6 mt-4 h-8 md:h-10 bg-stone-50 rounded-lg shrink-0"></div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto min-h-0 p-3 md:p-6">
              {/* Task Details Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-6 md:mb-8">
                {/* Left Column */}
                <div className="space-y-4 md:space-y-6">

                  {/* Status */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-20 md:w-24">
                      <Activity className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-semibold text-neutral-800">Status</label>
                    </div>
                    <div className="flex-1 flex items-center gap-1">
                      <select required
                        value={formData.status || ''}
                        onChange={(e) => updateField('status', e.target.value)}
                        className={`flex-1 px-3 py-2 text-xs font-semibold uppercase rounded-md focus:ring-2 focus:ring-blue-500 appearance-none ${getColorClasses(getStatusOption(formData.status).color)}`}
                      >
                        {statusOptions.map(status => (
                          <option key={status.value} value={status.value} className="bg-white text-gray-800">
                            {status.label.toUpperCase()}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-20 md:w-24">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-semibold text-neutral-800">Dates</label>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row gap-2 text-sm items-start sm:items-center mb-3">
                        <div className="flex items-center gap-1 text-neutral-400">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>Start</span>
                        </div>
                        <input
                          type="datetime-local"
                          value={formatForDatetimeLocal(formData.startDate)}
                          onChange={(e) => updateField('startDate', e.target.value)}
                          className="text-sm border border-gray-200 rounded px-2 py-1 text-neutral-600 w-full sm:w-auto"
                        />
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2 text-sm items-start sm:items-center">
                        <div className="flex items-center gap-1 text-red-600">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>End</span>
                          <input
                            type="datetime-local"
                            value={formatForDatetimeLocal(formData.endDate)}
                            onChange={(e) => updateField('endDate', e.target.value)}
                            className="text-sm border border-gray-200 rounded px-2 py-1 text-red-600 w-full sm:w-auto"
                          />
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Time Estimate */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-20 md:w-24">
                      <Clock className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-semibold text-neutral-800">Estimated Hours</label>
                    </div>
                    <input
                      type="text"
                      value={formData.timeEstimate || ''}
                      onChange={(e) => updateField('timeEstimate', e.target.value)}
                      placeholder="e.g., 8h, 2d"
                      className="flex-1 text-sm border border-gray-200 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Hold/Resume */}
                  {isEditing && formData.id && formData.status === 'Inprogress' && (
                    <button
                      type="button"
                      onClick={handleToggleHold}
                      disabled={holdLoading}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors self-start ${
                        holdLoading
                          ? 'opacity-60 cursor-not-allowed border-gray-200 text-gray-400'
                          : isOnHold
                            ? 'border-green-200 text-green-700 hover:bg-green-50'
                            : 'border-amber-200 text-amber-700 hover:bg-amber-50'
                      }`}
                    >
                      {isOnHold ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                      {isOnHold ? 'Resume Task' : 'Hold Task'}
                    </button>
                  )}

                  {/* Relationships */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-20 md:w-24">
                      <Link className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-semibold text-neutral-800">Links</label>
                    </div>
                    <input
                      type="text"
                      value={Array.isArray(formData.links) ? formData.links.join(', ') : (formData.links || '')}
                      onChange={(e) => updateField('links', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      placeholder="TASK-123, TASK-456"
                      className="flex-1 text-sm border border-gray-200 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4 md:space-y-6">
                  {/* Assignees */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-20 md:w-24">
                      <Users className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-semibold text-neutral-800">Team</label>
                    </div>
                    <div className="flex-1 relative" ref={assigneeDropdownRef}>
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-2">
                          {formData.assignees?.map((assignee, index) => (
                            <div
                              key={assignee.id}
                              className={`relative w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-xs font-semibold text-white ${assignee.color} group cursor-pointer`}
                              style={{ zIndex: formData.assignees.length - index }}
                              title={assignee.name}
                              onClick={() => removeAssignee(assignee.id)}
                            >
                              {assignee.initials}
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                ×
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                            className="w-7 h-7 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-gray-400"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {showAssigneeDropdown && (
                        <div className="absolute top-full left-0 mt-1 w-full sm:w-64 bg-white border rounded-lg shadow-lg z-50">
                          <div className="p-2 border-b">
                            <div className="relative">
                              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                              <input
                                type="text" required
                                value={assigneeSearch}
                                onChange={(e) => setAssigneeSearch(e.target.value)}
                                placeholder="Search employees..."
                                className="w-full pl-10 pr-4 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {filteredEmployees.filter(employee => employee.status === 'Active').map(employee => (
                              <div
                                key={employee.empId}
                                onClick={() => addAssignee(employee)}
                                className="p-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                              >
                                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-semibold">
                                  {employee.first_name[0]}{employee.last_name[0]}
                                </div>
                                <span>{employee.first_name} {employee.last_name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Priority */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-20 md:w-24">
                      <Flag className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-semibold text-neutral-800">Priority</label>
                    </div>
                    <select
                      value={formData.priority || ''} required
                      onChange={(e) => updateField('priority', e.target.value)}
                      className={`flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 font-medium ${formData.priority ? getColorClasses(getPriorityOption(formData.priority).color) : 'bg-gray-100 text-gray-800'}`}
                    >
                      {priorityOptions.map(priority => (
                        <option key={priority.value} value={priority.value} className="bg-white text-gray-800">
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sprint Points */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-20 md:w-24">
                      <Target className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-semibold text-neutral-800">Points</label>
                    </div>
                    <input
                      type="number"
                      value={formData.sprintPoints || ''}
                      onChange={(e) => updateField('sprintPoints', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      min="0"
                      placeholder="Enter points"
                    />
                  </div>

                  {/* Project Selection */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-20 md:w-24">
                      <FileText className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-semibold text-neutral-800">Project</label>
                    </div>
                    <select
                      value={formData.projectId || ''} required
                      onChange={(e) => updateField('projectId', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Project</option>
                      {projects.length > 0 ? (
                        projects.map((proj) => (
                          <option key={proj.id} value={proj.id}>
                            {proj.project_name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No Projects Found</option>
                      )}
                    </select>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-col sm:flex-row sm:items-start gap-2 md:gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-20 md:w-24 mt-0 sm:mt-2">
                      <Tag className="w-4 h-4 text-gray-600" />
                      <label className="text-sm font-semibold text-neutral-800">Tags</label>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.tags?.map(tag => (
                          <span
                            key={tag.id}
                            className={`px-3 py-1 rounded-full text-white text-xs font-semibold flex items-center gap-2 ${tag.color}`}
                          >
                            {tag.name}
                            <button
                              type="button"
                              onClick={() => removeTag(tag.id)}
                              className="hover:bg-black hover:bg-opacity-20 rounded-full w-4 h-4 flex items-center justify-center"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleAddTag}
                        placeholder="Type and press Enter to add tags..."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Description Section */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-4 h-4 text-gray-600" />
                  <h3 className="text-base font-bold text-neutral-800">Description</h3>
                </div>
                <div className="border border-zinc-100 rounded-lg p-4 md:p-6 bg-gray-50">
                  <textarea
                    value={formData.description || ''} required
                    onChange={(e) => updateField('description', e.target.value)}
                    className={`w-full bg-transparent border-none outline-none resize-none text-base leading-loose text-neutral-800 ${!showFullDescription ? 'h-32' : 'h-64'}`}
                    placeholder="Enter task description..."
                  />
                  <button
                    type="button"
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="mt-4 flex items-center gap-1.5 px-4 py-2 bg-white rounded-lg border text-sm font-semibold text-stone-500 hover:bg-gray-50"
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                    <span>{showFullDescription ? 'Show less' : 'Show more'}</span>
                  </button>
                </div>
              </div>

              {/* Images Section */}
              <div className="mb-6 md:mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <ImagePlus className="w-4 h-4 text-gray-600" />
                  <h3 className="text-base font-bold text-neutral-800">Images</h3>
                </div>

                <div
                  onDragEnter={() => setDragging(true)}
                  onDragLeave={() => setDragging(false)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleImageDrop}
                  onClick={() => imageInputRef.current?.click()}
                  className={`relative flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg px-4 py-5 cursor-pointer transition-colors select-none
                    ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-400 hover:bg-gray-50'}
                    ${pasteFlash ? 'border-green-500 bg-green-50' : ''}`}
                >
                  <input
                    ref={imageInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageInputChange}
                  />
                  <ImagePlus className="w-5 h-5 text-gray-400" />
                  <p className="text-sm text-gray-500 text-center leading-snug">
                    <span className="font-medium text-blue-600">Click to browse</span>{' '}
                    or drag &amp; drop
                  </p>
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <Clipboard className="w-3 h-3" />
                    Press{' '}
                    <kbd className="bg-gray-100 border border-gray-300 rounded px-1 py-0.5 text-[10px] font-mono">
                      Ctrl+V
                    </kbd>{' '}
                    to paste a screenshot
                  </p>
                  {pasteFlash && (
                    <span className="absolute top-2 right-3 text-xs text-green-600 font-semibold animate-pulse">
                      Pasted!
                    </span>
                  )}
                </div>

                {(formData.images?.length > 0 || newImagePreviews.length > 0) && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-3">
                    {formData.images?.map((img, index) => (
                      <div
                        key={`existing-${index}`}
                        className="relative border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition group"
                      >
                        <img
                          src={getImageUrl(img)}
                          alt={`task-${index}`}
                          onClick={() => setSelectedImage(getImageUrl(img))}
                          className="w-full h-32 object-cover hover:scale-105 transition cursor-pointer"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeExistingImage(index);
                          }}
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-red-600 transition opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {newImagePreviews.map((src, index) => (
                      <div
                        key={`new-${index}`}
                        className="relative border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition group"
                      >
                        <img
                          src={src}
                          alt={`new-upload-${index}`}
                          onClick={() => setSelectedImage(src)}
                          className="w-full h-32 object-cover hover:scale-105 transition cursor-pointer"
                        />
                        <span className="absolute bottom-1 left-1 bg-blue-600/90 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                          New
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNewImage(index);
                          }}
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-red-600 transition opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedImage && (
                <div
                  className="fixed inset-0 z-[10000] bg-black bg-opacity-80 flex items-center justify-center p-4"
                  onClick={() => setSelectedImage(null)}
                >
                  <div className="relative max-w-5xl w-full flex justify-center">
                    <img
                      src={selectedImage}
                      alt="Zoomed"
                      className="max-h-[90vh] w-auto rounded-lg shadow-lg"
                    />

                    <button
                      onClick={() => setSelectedImage(null)}
                      className="absolute top-2 right-2 bg-white rounded-full p-2 shadow"
                    >
                      <X className="w-5 h-5 text-black" />
                    </button>
                  </div>
                </div>
              )}

              <div className="mb-6 md:mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-4 h-4 text-gray-600" />
                  <h3 className="text-base font-bold text-neutral-800">Comments</h3>
                </div>

                <div className="border border-zinc-100 rounded-lg p-4 md:p-6 bg-gray-50">
                  <textarea
                    value={formData.comments || ''}
                    onChange={(e) => updateField('comments', e.target.value)}
                    className="w-full bg-transparent border-none outline-none resize-none text-base leading-loose text-neutral-800 h-16"
                    placeholder="Enter task comment..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700'
                  }`}
                >
                  {isSubmitting && (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Task' : 'Create Task')}
                </button>
              </div>
          </div>
          </form>
        </div>

        {/* Comments Sidebar */}
        <div className={`bg-stone-50 flex flex-col transition-all duration-300 ${
          isMobile
            ? showSidebar
              ? 'fixed inset-0 z-20'
              : 'hidden'
            : showSidebar
              ? 'w-80 border-l'
              : 'hidden'
        }`}>
          {/* Activity Header */}
          <div className="bg-white border-b">
            <div className="flex items-center justify-between p-4">
              <h3 className="text-xl font-semibold text-neutral-800">Activity</h3>
              <button
                onClick={toggleSidebar}
                className="p-1 hover:bg-gray-100 rounded"
                title="Close Comments"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Activity Stream */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
            {comments.map(comment => (
              <div key={comment.id}>
                {comment.type === 'system' ? (
                  <div className="flex gap-3.5">
                    <div className="font-bold leading-none text-center text-neutral-400">•</div>
                    <div className="flex gap-0.5 text-zinc-500 flex-1">
                      <span className="leading-none">{comment.user}</span>
                      <span className="leading-none">{comment.action}</span>
                    </div>
                    <div className="text-zinc-500 leading-none text-right">
                      {formatDateTime(comment.timestamp)}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-3 rounded-lg border">
                    <div className="font-medium text-gray-800">{comment.user}</div>
                    <div className="mt-1">{comment.message}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {formatDateTime(comment.timestamp)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Comment Input */}
          <div className="border-t bg-white">
            <form onSubmit={handleCommentSubmit} className="p-4">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-2.5">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="w-full p-3 border-none outline-none resize-none text-sm text-neutral-400"
                  rows={3}
                />
                <div className="flex justify-between items-center mt-2">
                  <div className="text-xs text-gray-500">
                    Press Enter to send
                  </div>
                  <button
                    type="submit"
                    disabled={!comment.trim()}
                    className="px-4 py-2 bg-blue-600 rounded-lg flex items-center justify-center disabled:opacity-50 text-white text-sm font-medium"
                  >
                    Send
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;